import { ProductionFilesService } from '@services/production-files.service';
import { DocViewerParamReader } from './doc-viewer-param-reader';
import { iFrameUrlFactory } from './iframe-url-factory';
import { ProductionJobDocsService } from '@services/production-job-docs.service';
import { Injectable } from '@angular/core';
import { finalize, forkJoin, from, map, mergeMap, Observable, of, switchMap } from 'rxjs';
import { DocViewerStore } from '@store/legacy/doc-viewer.store';
import { ProductionJobSdk } from '@sdks/production-job.sdk';

@Injectable({
	'providedIn': 'root'
})
export class ProductionManager {

	/** Selected productionJobId */
	public selectedProductionJobId: string;

	/** [productionJobId, title] */
	public productions: [string, string][] = [];

	/** Doc ID corresponding to these productions. undefined if not yet loaded. */
	public docId: string;

	constructor(
		private filesService: ProductionFilesService,
		private jobSdk: ProductionJobSdk,
		private jobDocsService: ProductionJobDocsService,
		private iframeUrls: iFrameUrlFactory,
		private paramReader: DocViewerParamReader,
		private docViewerStore: DocViewerStore
	) {

	}

	/** Loads production files for the current document.
	 *  If productions for the current document have already been loaded, method returns immediately.
	 *  If the 'prodId' query parameter is specified, the corresponding production will be selected, if found.
	 *  If not found, or no 'prodId' param is specified, the first production is selected.
	 *  This method does not return until all productions are loaded and a selection is made.
	 */
	public load(documentId: string): Observable<void> {
		if (documentId && documentId === this.docId)
			return of(null); // Productions are already loaded for this doc
		this.docId = documentId;
		return this.filesService.getProductionFiles(documentId).pipe(
			switchMap((files) => from(files)),
			mergeMap((file) => {
				this.productions = [];
				return forkJoin([
					this.jobSdk.get({ id: file.productionJobId }),
					this.jobDocsService.getProductionJobDocs(this.paramReader.docId, file.productionJobId)
				]).pipe(
					map(([productionJob, productionJobDocs]) => {
						const job = productionJob;
						const doc = productionJobDocs;
						this.productions.push([file.productionJobId, `${doc.batesPrefix} ${doc.batesNumberStart.toString().padStart(doc.batesNumberDigits, '0')} (${job.title})`]);
					})
				);
			}),
			finalize(() => {
				let matchingProd: [string, string];
				if (this.paramReader.prodId) {
					matchingProd = this.productions.find(prod => prod[0] === this.paramReader.prodId);
					if (!matchingProd) console.warn(`Could not find production specified in query: '${this.paramReader.prodId}'.`);
				}
				const newSelection: string = matchingProd?.[0] || this.productions?.find(prod => prod[0] === this.selectedProductionJobId)?.[0] || this.productions?.[0]?.[0] || this.selectedProductionJobId;
				if (this.selectedProductionJobId !== newSelection) {
					this.selectedProductionJobId = newSelection;
				}
				if (this.selectedProductionJobId && this.productions)
					this.updateDocViewerStoreState(this.selectedProductionJobId, this.productions);
			})
		);
	}

	public selectionChanged(fromNewDocViewer: boolean = false) {
		if (!fromNewDocViewer) {
			this.iframeUrls.updateProductionId(this.selectedProductionJobId);
			return;
		}
		this.updateDocViewerStoreState(this.selectedProductionJobId);
	}

	public reset(): void {
		this.docId = null;
		this.productions = [];
	}

	private updateDocViewerStoreState(id: string, fileTitles: [string, string][] = null) {
		this.docViewerStore.updateState((currentState) => {
			currentState.productionId = id;
			currentState.productionDocs = fileTitles;
			return currentState;
		});
	}
}
